import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import useSessionContext from '../../../data/queryHooks/useSessionContext';
import {
	getRedirectionLink,
	getHomePagePath,
} from '../../../scripts/navigationHelpers';
import {
	retrievePermissionWithKey,
	retrievePageKeyWithRoute,
} from '../../../scripts/pageConstants';
import { HOME_URL } from '../../../scripts/navigationItems';
import { flowPaths } from '../../../scripts/flowPaths';

const ProtectedRoute = ({ children = null }) => {
	const {
		isLoading,
		isLoggedIn,
		permissions,
		redirectionLinks,
		flows: incompleteFlows,
	} = useSessionContext();

	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		// don't do anything if the session data is loading
		if (isLoading) {
			return;
		}

		if (!permissions || !redirectionLinks) {
			return;
		}

		const homeUrl =
			getHomePagePath(permissions, redirectionLinks) || HOME_URL;

		// redirect the user to the home page if user isn't logged in
		if (!isLoggedIn) {
			window.open(homeUrl, '_self', 'noreferrer');
			return;
		}

		// Redirect the user to the home page if they are not allowed to access this page
		// Any page that doesn't have a page key is permitted to pass
		const currentPageKey =
			retrievePageKeyWithRoute[location.pathname.toLowerCase()];

		const permissionsToFind = retrievePermissionWithKey[currentPageKey];
		const canUserAccessThisPage = permissions.some(a =>
			permissionsToFind?.includes(a)
		);

		if (currentPageKey && !canUserAccessThisPage) {
			window.open(homeUrl, '_self', 'noreferrer');
			return;
		}

		// Find the highest priority unskippable flow item
		const highestPriorityIncompleteFlow = incompleteFlows
			.filter(flows => !flows.canSkip)
			.reduce(
				(min, current) =>
					current.priority < min.priority ? current : min,
				incompleteFlows.length > 0 ? incompleteFlows[0] : null
			);

		// Get url for the first non-completed flow from flowPaths
		const firstIncompleteFlowPath = flowPaths.find(
			flowPath => flowPath?.type === highestPriorityIncompleteFlow?.type
		);

		// Redirect the user to the first incomplete flow.  Avoid redirect loop if they're already on the page
		if (
			location.pathname !== firstIncompleteFlowPath.url &&
			highestPriorityIncompleteFlow?.flow ===
				firstIncompleteFlowPath?.flow
		) {
			navigate(firstIncompleteFlowPath.url);
			return;
		}

		// Redirect the user if the redirection link is available
		const link = getRedirectionLink(currentPageKey, redirectionLinks);
		if (link) {
			if (link.includes('.aspx')) {
				window.open(link, '_self', 'noreferrer');
				return;
			}

			navigate(link);
		}

		// If all checks pass, allow user to navigate to this page
	}, [location.pathname, isLoggedIn]);

	return children || <Outlet />;
};

export default ProtectedRoute;

ProtectedRoute.propTypes = {
	children: PropTypes.element,
};
